/**
 * bootLogger.ts — Structured Boot Event Collector™ (Task #38)
 *
 * Singleton that records every boot lifecycle event in a structured format.
 * Boot sequences in Shell.tsx, useFirstBoot.ts, and HydrationOrchestrator
 * call bootLogger.record() instead of raw console.log so the Boot Sequence
 * Inspector™ in DeveloperSettings can render a live timeline.
 *
 * Console output format: `[BOOT] step=<name> status=ok|fail|pending|skip ms=<n>`
 *
 * Design rules:
 *  - Module-level singleton — one per page lifetime.
 *  - record() is synchronous and never throws.
 *  - Subscribers are called synchronously after each event.
 *  - Max 200 entries (ring-buffer) to avoid unbounded memory growth.
 */

export type BootStatus = 'pending' | 'ok' | 'fail' | 'skip';

export interface BootEvent {
  /** Stable machine-readable identifier for the boot step. */
  step: string;
  status: BootStatus;
  /** Human-readable short label shown in the inspector. */
  label: string;
  /** Optional detail — e.g. userId, error message, flag values. */
  detail?: string;
  /** Wall-clock time when the event was recorded. */
  at: number;
  /** Elapsed ms since page load (performance.now()). */
  ms: number;
}

type BootSubscriber = (events: BootEvent[]) => void;

// Pre-declared step labels so the inspector can show "pending" for steps that
// haven't fired yet. Order matches the boot lifecycle sequence.
// 11 steps — every step is concretely instrumented somewhere in the boot path.
// `academy` was removed: it has no concrete boot event (it's a lazy-loaded route,
// not a pipeline step). Keeping unimplementable steps makes isComplete unreachable.
export const BOOT_STEPS: { step: string; label: string }[] = [
  { step: 'supabase-init',    label: 'Supabase session check'     },
  { step: 'jwt-verified',     label: 'JWT access token present'   },
  { step: 'internal-user',    label: 'Internal user resolved'     },
  { step: 'onboarding-check', label: 'Onboarding flag from server'},
  { step: 'persona-load',     label: 'Persona loaded from storage'},
  { step: 'cloud-hydration',  label: 'Cloud hydration complete'   },
  { step: 'location-set',     label: 'Current location established'},
  { step: 'boot-decision',    label: 'Boot experience decided'    },
  { step: 'opportunities',    label: 'Opportunities ready'        },
  { step: 'mission-pipeline', label: 'Mission pipeline complete'  },
  { step: 'dashboard-ready',  label: 'Dashboard ready'            },
];

const MAX_EVENTS = 200;

class BootLogger {
  private events: BootEvent[] = [];
  private subscribers: Set<BootSubscriber> = new Set();
  private pageStart: number = performance.now();

  /** Record a boot event and emit console output. Never throws. */
  record(step: string, status: BootStatus, detail?: string): void {
    try {
      const label = BOOT_STEPS.find(s => s.step === step)?.label ?? step;
      const ms    = Math.round(performance.now() - this.pageStart);
      const event: BootEvent = { step, status, label, at: Date.now(), ms, detail };

      // Console output — matches the structured format spec.
      const detailStr = detail ? ` detail="${detail}"` : '';
      console.log(`[BOOT] step=${step} status=${status} ms=${ms}${detailStr}`);

      // Ring-buffer: keep at most MAX_EVENTS.
      if (this.events.length >= MAX_EVENTS) this.events.shift();
      this.events.push(event);

      // Notify subscribers synchronously (they may setState — this is fine from
      // useEffect / event handlers; never call record() from inside a render).
      const snapshot = [...this.events];
      this.subscribers.forEach(cb => { try { cb(snapshot); } catch { /* ignore */ } });
    } catch {
      /* record() must never throw */
    }
  }

  /** Return current event snapshot. */
  getLogs(): BootEvent[] {
    return [...this.events];
  }

  /** Clear all recorded events (used by the "Reset" button in the inspector). */
  clear(): void {
    this.events = [];
    const snapshot: BootEvent[] = [];
    this.subscribers.forEach(cb => { try { cb(snapshot); } catch { /* ignore */ } });
  }

  /** Subscribe to event updates. Returns an unsubscribe function. */
  subscribe(cb: BootSubscriber): () => void {
    this.subscribers.add(cb);
    // Immediately deliver current state.
    try { cb([...this.events]); } catch { /* ignore */ }
    return () => { this.subscribers.delete(cb); };
  }
}

export const bootLogger = new BootLogger();
